export interface GleanScopeContext {
  workspace?: { id: string; name: string };
  client?: { id: string; name: string };
  engagement?: { id: string; name: string };
  project?: { id: string; name: string };
  application?: { id: string; name: string };
  page_url: string;
}

export interface GleanTable {
  caption?: string;
  headers: string[];
  rows: string[][];
  truncated: boolean;
}

export interface GleanCollectedContext {
  scope: GleanScopeContext;
  capture_method: "explicit_selection" | "structured_selection" | "active_content";
  selected_text?: string;
  active_content?: string;
  current_chat_title?: string;
  relevant_user_question?: string;
  research_question?: string;
  tables: GleanTable[];
  source_references: Array<{ label: string; url?: string; id?: string }>;
  omissions: string[];
}

export interface GleanContextProvider {
  id: string;
  priority: number;
  collect: () => Partial<GleanCollectedContext> | null;
}

const providers = new Map<string, GleanContextProvider>();
const MAX_TEXT = 20_000;

export function registerGleanContextProvider(provider: GleanContextProvider): () => void {
  providers.set(provider.id, provider);
  return () => providers.delete(provider.id);
}

export function snapshotSelection(documentRef: Document = document): string {
  return (documentRef.defaultView?.getSelection()?.toString() ?? "").trim();
}

function visible(element: Element): boolean {
  for (let node: Element | null = element; node; node = node.parentElement) {
    const style = node.ownerDocument.defaultView?.getComputedStyle(node);
    if ((node as HTMLElement).hidden || node.getAttribute("aria-hidden") === "true"
      || style?.display === "none" || style?.visibility === "hidden") return false;
  }
  return true;
}

function tableData(table: HTMLTableElement): GleanTable {
  const headers = Array.from(table.querySelectorAll("thead th")).map((cell) => (cell.textContent ?? "").trim().slice(0, 500));
  const rows = Array.from(table.querySelectorAll("tbody tr")).slice(0, 100).map((row) =>
    Array.from(row.querySelectorAll("th,td")).map((cell) => (cell.textContent ?? "").trim().slice(0, 2_000)),
  );
  return {
    caption: table.caption?.textContent?.trim() || undefined,
    headers,
    rows,
    truncated: table.querySelectorAll("tbody tr").length > rows.length,
  };
}

export function collectGleanContext(
  scope: GleanScopeContext,
  preservedSelection: string,
  documentRef: Document = document,
  selectionRoot?: Element | null,
): GleanCollectedContext | null {
  const provided = !preservedSelection && Array.from(providers.values()).sort((a, b) => b.priority - a.priority)
    .map((provider) => provider.collect()).find(Boolean);
  if (provided) {
    return {
      capture_method: provided.capture_method ?? "structured_selection",
      tables: provided.tables ?? [], source_references: provided.source_references ?? [],
      omissions: provided.omissions ?? [], ...provided, scope,
    };
  }

  const selectedRoots = Array.from(documentRef.querySelectorAll(
    '[data-glean-selected="true"], [aria-selected="true"][data-glean-context]',
  )).filter(visible);
  const activeRoots = Array.from(documentRef.querySelectorAll(
    '[data-glean-context-active="true"], [data-glean-response-active="true"]',
  )).filter(visible);
  const roots = preservedSelection
    ? selectionRoot && visible(selectionRoot) ? [selectionRoot] : []
    : selectedRoots.length ? selectedRoots : activeRoots.slice(-1);
  const activeText = roots.map((root) => (root.textContent ?? "").trim()).filter(Boolean).join("\n\n");
  const text = preservedSelection || activeText;
  if (!text) return null;
  const omissions: string[] = [];
  if (text.length > MAX_TEXT) omissions.push(`Content truncated after ${MAX_TEXT} characters.`);
  if (roots.some((root) => Boolean(root.querySelector("canvas,img,svg")))) {
    omissions.push("Visual content was represented only by available captions or underlying table data; OCR/vision was not used.");
  }
  const allTables = roots.flatMap((root) => Array.from(root.querySelectorAll("table")).map(tableData));
  const tables = allTables.slice(0, 20);
  if (allTables.length > tables.length) omissions.push("Additional tables were omitted after the 20-table limit.");
  const sourceReferences = roots.flatMap((root) => Array.from(root.querySelectorAll("a[href]")).slice(0, 50).map((link) => ({
    label: (link.textContent ?? "").trim() || "Linked source",
    url: (link as HTMLAnchorElement).href,
    id: link.getAttribute("data-source-id") ?? undefined,
  })));
  return {
    scope,
    capture_method: preservedSelection
      ? "explicit_selection"
      : selectedRoots.length ? "structured_selection" : "active_content",
    selected_text: preservedSelection ? preservedSelection.slice(0, MAX_TEXT) : undefined,
    active_content: preservedSelection ? activeText.slice(0, 4_000) || undefined : text.slice(0, MAX_TEXT),
    current_chat_title: roots.at(-1)?.getAttribute("data-glean-chat-title") || undefined,
    relevant_user_question: (roots.at(-1)?.querySelector("[data-glean-user-question]")?.textContent ?? "").trim() || undefined,
    tables,
    source_references: sourceReferences,
    omissions,
  };
}
